import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Copy, Pencil, Save, X, Trash2, AlertCircle, Clock } from 'lucide-react';
import { weeklyArApApi } from '../../services/weeklyArApApi';
import {
    NON_BC_DEFAULT_CURRENCY,
    type WeeklyArApBalance,
} from '../../types/bankBalance';
import { SUPPORTED_CURRENCIES } from '../../types/cashCockpit';
import { ConfirmModal } from '../ui/ConfirmModal';

interface Props {
    companyCode: string;
    canEdit: boolean;
}

interface DraftRow {
    postingDate: string;
    currencyCode: string;
    arAmountLCY: string;
    apAmountLCY: string;
    comment: string;
}

function formatAmount(n: number | null | undefined): string {
    if (n == null) return '—';
    return new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}
function formatDate(d: string): string {
    return new Date(d).toLocaleDateString('de-CH', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Return next Friday (ISO YYYY-MM-DD), or today if today is Friday. */
function nextFriday(): string {
    const d = new Date();
    const dayDiff = (5 - d.getDay() + 7) % 7; // 5 = Friday
    d.setDate(d.getDate() + dayDiff);
    return d.toISOString().slice(0, 10);
}

function daysBetween(iso: string, now: Date): number {
    const d = new Date(iso);
    return Math.floor((now.getTime() - d.getTime()) / 86_400_000);
}

function StalenessBadge({ lastUpdateIso }: { lastUpdateIso: string | null }) {
    if (!lastUpdateIso) {
        return (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                <Clock className="w-3 h-3" /> No entries yet
            </span>
        );
    }
    const days = daysBetween(lastUpdateIso, new Date());
    const severity = days > 14 ? 'critical' : days > 7 ? 'warning' : 'ok';
    const color = severity === 'critical'
        ? 'bg-red-50 text-red-700 border-red-200'
        : severity === 'warning'
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : 'bg-green-50 text-green-700 border-green-200';
    return (
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${color}`}>
            <Clock className="w-3 h-3" /> Last update {days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'} ago`}
        </span>
    );
}

export function ArApBalanceTab({ companyCode, canEdit }: Props) {
    const defaultCcy = NON_BC_DEFAULT_CURRENCY[companyCode] ?? 'EUR';

    const [entries, setEntries] = useState<WeeklyArApBalance[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [draft, setDraft] = useState<DraftRow | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editBuffer, setEditBuffer] = useState<DraftRow | null>(null);
    const [deleteId, setDeleteId] = useState<number | null>(null);

    const emptyDraft = useMemo<DraftRow>(() => ({
        postingDate: nextFriday(),
        currencyCode: defaultCcy,
        arAmountLCY: '',
        apAmountLCY: '',
        comment: '',
    }), [defaultCcy]);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await weeklyArApApi.list(companyCode);
            setEntries(data);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load entries');
        } finally {
            setLoading(false);
        }
    }, [companyCode]);

    useEffect(() => { void reload(); }, [reload]);

    const latest = entries[0] ?? null;
    const lastUpdate = latest ? (latest.UpdatedAt ?? latest.CreatedAt) : null;

    const handleSaveNew = async () => {
        if (!draft) return;
        const ar = parseFloat(draft.arAmountLCY);
        const ap = parseFloat(draft.apAmountLCY);
        if (!isFinite(ar) || ar < 0) { setError('AR must be a non-negative number'); return; }
        if (!isFinite(ap) || ap < 0) { setError('AP must be a non-negative number'); return; }
        try {
            await weeklyArApApi.create({
                companyCode,
                postingDate: draft.postingDate,
                currencyCode: draft.currencyCode,
                arAmountLCY: ar,
                apAmountLCY: ap,
                comment: draft.comment || null,
            });
            setDraft(null);
            await reload();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Save failed');
        }
    };

    const startEdit = (e: WeeklyArApBalance) => {
        setEditingId(e.Id);
        setEditBuffer({
            postingDate: e.PostingDate.slice(0, 10),
            currencyCode: e.CurrencyCode,
            arAmountLCY: String(e.ArAmountLCY),
            apAmountLCY: String(e.ApAmountLCY),
            comment: e.Comment ?? '',
        });
    };

    const saveEdit = async () => {
        if (editingId == null || !editBuffer) return;
        const ar = parseFloat(editBuffer.arAmountLCY);
        const ap = parseFloat(editBuffer.apAmountLCY);
        if (!isFinite(ar) || ar < 0) { setError('AR must be a non-negative number'); return; }
        if (!isFinite(ap) || ap < 0) { setError('AP must be a non-negative number'); return; }
        try {
            await weeklyArApApi.update(editingId, {
                currencyCode: editBuffer.currencyCode,
                arAmountLCY: ar,
                apAmountLCY: ap,
                comment: editBuffer.comment || null,
            });
            setEditingId(null); setEditBuffer(null);
            await reload();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Update failed');
        }
    };

    const handleCopyLastWeek = async () => {
        if (!latest) { setError('Nothing to copy — add a first entry manually.'); return; }
        const target = new Date(latest.PostingDate);
        target.setDate(target.getDate() + 7);
        try {
            await weeklyArApApi.copyLastWeek(companyCode, target.toISOString().slice(0, 10));
            await reload();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Copy failed');
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await weeklyArApApi.delete(id);
            setDeleteId(null);
            await reload();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Delete failed');
            setDeleteId(null);
        }
    };

    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-4 py-3 border-b border-gray-200">
                <div>
                    <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">AR / AP Balance</h3>
                        <span className="inline-flex items-center text-[11px] font-medium uppercase tracking-wide bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5">
                            Company: {companyCode.toUpperCase()}
                        </span>
                        <StalenessBadge lastUpdateIso={lastUpdate} />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Weekly snapshot of open AR and AP in local currency. Feeds group-level working-capital reports + BAB calculated interest.
                        Enter one row per week (typically Friday).
                    </p>
                </div>
                {canEdit && (
                    <div className="flex gap-2 flex-shrink-0">
                        <button
                            onClick={handleCopyLastWeek}
                            disabled={!latest}
                            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Copy AR/AP from the most recent entry to next week"
                        >
                            <Copy className="w-4 h-4" /> Copy last week
                        </button>
                        {!draft && (
                            <button
                                onClick={() => setDraft({ ...emptyDraft })}
                                className="inline-flex items-center gap-1 text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                <Plus className="w-4 h-4" /> New Week
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Mini KPI strip */}
            {latest && (
                <div className="flex flex-wrap gap-6 px-4 py-3 bg-gray-50 border-b border-gray-200 text-sm">
                    <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Current AR</div>
                        <div className="font-semibold font-mono text-[#2C7BB6]">{formatAmount(latest.ArAmountLCY)} {latest.CurrencyCode}</div>
                    </div>
                    <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Current AP</div>
                        <div className="font-semibold font-mono text-[#D7191C]">{formatAmount(latest.ApAmountLCY)} {latest.CurrencyCode}</div>
                    </div>
                    <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Working Capital (AR + AP)</div>
                        <div className="font-semibold font-mono text-gray-900">{formatAmount(latest.ArAmountLCY + latest.ApAmountLCY)} {latest.CurrencyCode}</div>
                    </div>
                    <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide">As of</div>
                        <div className="font-semibold font-mono text-gray-900">{formatDate(latest.PostingDate)}</div>
                    </div>
                </div>
            )}

            {error && (
                <div className="mx-4 my-2 flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded p-2 text-sm">
                    <AlertCircle className="w-4 h-4" /> {error}
                </div>
            )}

            {/* Table */}
            <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-600">
                    <tr>
                        <th className="px-3 py-2">Week Ending</th>
                        <th className="px-3 py-2">CCY</th>
                        <th className="px-3 py-2 text-right">AR (LCY)</th>
                        <th className="px-3 py-2 text-right">AP (LCY)</th>
                        <th className="px-3 py-2 text-right">Working Capital</th>
                        <th className="px-3 py-2">Comment</th>
                        <th className="px-3 py-2 w-24">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {draft && (
                        <tr className="bg-blue-50 border-t border-blue-100">
                            <td className="px-3 py-2">
                                <input type="date" value={draft.postingDate}
                                    onChange={e => setDraft({ ...draft, postingDate: e.target.value })}
                                    className="border border-gray-300 rounded px-1 py-0.5 text-xs"
                                />
                            </td>
                            <td className="px-3 py-2">
                                <select value={draft.currencyCode}
                                    onChange={e => setDraft({ ...draft, currencyCode: e.target.value })}
                                    className="border border-gray-300 rounded px-1 py-0.5 text-xs w-16">
                                    {SUPPORTED_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </td>
                            <td className="px-3 py-2 text-right">
                                <input type="number" step="0.01" min="0" placeholder="0.00" value={draft.arAmountLCY}
                                    onChange={e => setDraft({ ...draft, arAmountLCY: e.target.value })}
                                    className="border border-gray-300 rounded px-1 py-0.5 text-xs text-right w-32"
                                />
                            </td>
                            <td className="px-3 py-2 text-right">
                                <input type="number" step="0.01" min="0" placeholder="0.00" value={draft.apAmountLCY}
                                    onChange={e => setDraft({ ...draft, apAmountLCY: e.target.value })}
                                    className="border border-gray-300 rounded px-1 py-0.5 text-xs text-right w-32"
                                />
                            </td>
                            <td className="px-3 py-2 text-right text-gray-400 font-mono">
                                {formatAmount((parseFloat(draft.arAmountLCY) || 0) + (parseFloat(draft.apAmountLCY) || 0))}
                            </td>
                            <td className="px-3 py-2">
                                <input type="text" placeholder="optional note" value={draft.comment}
                                    onChange={e => setDraft({ ...draft, comment: e.target.value })}
                                    className="border border-gray-300 rounded px-1 py-0.5 text-xs w-full"
                                />
                            </td>
                            <td className="px-3 py-2">
                                <div className="flex gap-1">
                                    <button onClick={handleSaveNew} className="text-green-600 hover:text-green-800" title="Save">
                                        <Save className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => setDraft(null)} className="text-gray-500 hover:text-gray-700" title="Cancel">
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    )}
                    {loading && entries.length === 0 && (
                        <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500 text-sm">Loading…</td></tr>
                    )}
                    {!loading && entries.length === 0 && !draft && (
                        <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500 text-sm">
                            No weekly entries yet. Click "New Week" to add the first snapshot.
                        </td></tr>
                    )}
                    {entries.map(e => {
                        const isEditing = editingId === e.Id;
                        const buf = isEditing ? editBuffer : null;
                        return (
                            <tr key={e.Id} className="border-t border-gray-100 hover:bg-gray-50">
                                <td className="px-3 py-2 text-xs">{formatDate(e.PostingDate)}</td>
                                <td className="px-3 py-2 text-xs">
                                    {isEditing && buf ? (
                                        <select value={buf.currencyCode}
                                            onChange={ev => setEditBuffer({ ...buf, currencyCode: ev.target.value })}
                                            className="border border-gray-300 rounded px-1 py-0.5 text-xs w-16">
                                            {SUPPORTED_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    ) : e.CurrencyCode}
                                </td>
                                <td className="px-3 py-2 text-xs font-mono tabular-nums text-right">
                                    {isEditing && buf ? (
                                        <input type="number" step="0.01" min="0" value={buf.arAmountLCY}
                                            onChange={ev => setEditBuffer({ ...buf, arAmountLCY: ev.target.value })}
                                            className="border border-gray-300 rounded px-1 py-0.5 text-xs text-right w-32"
                                        />
                                    ) : (
                                        <span className="text-[#2C7BB6]">{formatAmount(e.ArAmountLCY)}</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-xs font-mono tabular-nums text-right">
                                    {isEditing && buf ? (
                                        <input type="number" step="0.01" min="0" value={buf.apAmountLCY}
                                            onChange={ev => setEditBuffer({ ...buf, apAmountLCY: ev.target.value })}
                                            className="border border-gray-300 rounded px-1 py-0.5 text-xs text-right w-32"
                                        />
                                    ) : (
                                        <span className="text-[#D7191C]">{formatAmount(e.ApAmountLCY)}</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-xs font-mono tabular-nums text-right text-gray-700 font-medium">
                                    {formatAmount(
                                        isEditing && buf
                                            ? (parseFloat(buf.arAmountLCY) || 0) + (parseFloat(buf.apAmountLCY) || 0)
                                            : e.ArAmountLCY + e.ApAmountLCY
                                    )}
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-600 max-w-xs truncate">
                                    {isEditing && buf ? (
                                        <input type="text" value={buf.comment}
                                            onChange={ev => setEditBuffer({ ...buf, comment: ev.target.value })}
                                            className="border border-gray-300 rounded px-1 py-0.5 text-xs w-full"
                                        />
                                    ) : (e.Comment || '—')}
                                </td>
                                <td className="px-3 py-2 text-xs">
                                    {canEdit && (isEditing ? (
                                        <div className="flex gap-1">
                                            <button onClick={saveEdit} className="text-green-600 hover:text-green-800" title="Save">
                                                <Save className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => { setEditingId(null); setEditBuffer(null); }}
                                                className="text-gray-500 hover:text-gray-700" title="Cancel">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex gap-1">
                                            <button onClick={() => startEdit(e)} className="text-blue-600 hover:text-blue-800" title="Edit">
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => setDeleteId(e.Id)} className="text-red-600 hover:text-red-800" title="Delete">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {deleteId !== null && (
                <ConfirmModal
                    title="Delete weekly entry?"
                    message="This removes the weekly AR/AP snapshot. If you want to keep history, edit the values instead."
                    confirmLabel="Delete"
                    variant="danger"
                    onConfirm={() => handleDelete(deleteId)}
                    onCancel={() => setDeleteId(null)}
                />
            )}
        </div>
    );
}
